/**
 * 后端结构化 i18n 文本（lang + text + translations）的通用展示工具。
 *
 * 与 desktop-web/src/utils/i18nText.ts 保持同 API；mobile 端独立一份，避免跨子项目耦合。
 *
 * 适配场景：
 * - 审核评论 ReviewCommentParsed
 * - 客诉原因 ComplaintReasonParsed
 * - DSP 申诉理由 AppealReasonParsed
 * - 任何后端按 {lang, text, translations:[{lang,text}]} 结构返回的字段
 */

const SUPPORTED_LANGS = ['zh', 'en', 'es'] as const;

/** 后端结构化 i18n 文本的鸭子类型；客诉/审核等不同 domain 的具体类型都满足此契约 */
export interface LocalizedText {
    /** 原文（translations 无当前 locale 命中时回退展示用） */
    text: string;
    lang: string;
    translations: { lang: string; text: string }[];
}

/**
 * 按当前 locale 选最合适的展示文本：
 * - 优先取 translations 中匹配 locale 的译文
 * - 无命中（含展示语言 == 源语言、translations 通常不含源语言条目）则回退原文 text
 */
export function pickDisplayText(
    parsed: LocalizedText | null | undefined,
    currentLocale: string,
): string {
    if (!parsed) return '';
    return parsed.translations?.find((e) => e.lang === currentLocale)?.text || parsed.text || '';
}

/**
 * 把 i18n.language（可能 'zh-CN'/'en-US'）规整为后端/阿里云认识的纯 2 字母 code；
 * 不在白名单内的回落 'en'。
 */
export function normalizeLang(raw: string): string {
    const short = (raw || '').toLowerCase().split('-')[0];
    return (SUPPORTED_LANGS as readonly string[]).includes(short) ? short : 'en';
}

/**
 * 从可扩展多语言文案 texts:[{lang,text}] 按当前 locale 取展示文案：
 * - 优先匹配规整后的 locale；无命中回退 'en'；再回退首项；最后回退空串。
 * - 用于字典枚举（客诉状态/分类/审核驳回理由等）的 texts 结构，新增语言后端加项即可，前端不改。
 */
export function pickLocalizedText(
    texts: { lang: string; text: string }[] | undefined,
    rawLocale: string,
): string {
    if (!texts || texts.length === 0) return '';
    const locale = normalizeLang(rawLocale);
    return (
        texts.find((e) => e.lang === locale)?.text ??
        texts.find((e) => e.lang === 'en')?.text ??
        texts[0]?.text ??
        ''
    );
}

/** 后端审核评论结构化解析结果的鸭子类型：tagOptions 可扩展多语言文案 + LocalizedText 自由文本 */
export interface ReviewCommentLike extends LocalizedText {
    tagOptions: { texts: { lang: string; text: string }[] }[];
}

/**
 * 把后端结构化审核评论渲染为当前语言下的人话：tag 走后端字典 texts 按 locale 选；text 按 lang/translations locale 选；
 * 入参 parsed 为 null/undefined 返回 '-'。
 */
export function renderReviewComment(
    parsed: ReviewCommentLike | null | undefined,
    rawLocale: string,
): string {
    if (!parsed) return '-';
    const locale = normalizeLang(rawLocale);
    const displayText = pickDisplayText(parsed, locale);
    const tagTexts = parsed.tagOptions.map((opt) => pickLocalizedText(opt.texts, locale));
    const joined = [...tagTexts, displayText].filter((s) => s && s.length > 0).join('；');
    return joined || '-';
}
